import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { useLoggedParkingaboServerWrite } from '../../../api/ParkingaboApi';
import {
    VehicleForm,
    VehicleOnboardingPayload,
} from '../../../components/forms/VehicleForm';
import { ParkingaboAsyncLoadedSection } from '../../../components/layout/ParkingaboAsyncLoadedSection';
import { ParkingaboLayoutWithHeader } from '../../../components/layout/ParkingaboLayoutWithHeader';
import {
    makeCountryOptions,
    useCountryFetch,
} from '../../../hooks/UseCountryFetch';
import { ParkingaboRoutedModal } from '../../../components/layout/ParkingaboRoutedModal';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';

export function VehiclesDetailRoute() {
    const { vehicleId } = useParams<{ vehicleId: string }>();
    const { vehicles } = useCustomerContext();
    const navigate = useNavigate();

    const vehicle = useMemo(() => {
        return vehicles.data.find(v => v.customerTenantCarId === vehicleId);
    }, [vehicleId, vehicles.data]);

    const [countriesState] = useCountryFetch();
    const [refetchLoading, setRefetchLoading] = useState(false);

    const [state, submit] = useLoggedParkingaboServerWrite<
        VehicleOnboardingPayload,
        never
    >('edit-vehicle', () => ({
        url: `user/self/vehicles/${vehicleId}`,
        method: RequestMethod.PUT,
    }));

    const [vehicleDeleteState, deleteVehicle] = useLoggedParkingaboServerWrite<
        { vehicleId: string },
        never
    >('delete-vehicle', context => ({
        url: `user/self/vehicles/${context.vehicleId}`,
        method: RequestMethod.DELETE,
    }));

    useServerSuccessEffect(state, () => {
        vehicles.refetch();
        setRefetchLoading(true);
    });

    useEffect(() => {
        if (refetchLoading && vehicles.status === RequestStatus.SUCCESS) {
            navigate('..');
        }
    }, [navigate, refetchLoading, vehicles.status]);

    useServerSuccessEffect(vehicleDeleteState, () => {
        vehicles.refetch();
        navigate('..', { replace: true });
    });

    useEffect(() => {
        if (!vehicle) {
            navigate('..', { replace: true });
        }
    }, [navigate, vehicle]);

    function handleVehicleDelete() {
        if (vehicleId) {
            deleteVehicle({ vehicleId });
        }
    }

    if (!vehicle) {
        return null;
    }

    return (
        <ParkingaboRoutedModal
            open={true}
            backUrl={'..'}
            render={() => (
                <ParkingaboLayoutWithHeader
                    backTo={'..'}
                    title={vehicle.description}
                >
                    <ParkingaboAsyncLoadedSection
                        state={countriesState}
                        render={data => {
                            return (
                                <>
                                    <VehicleForm
                                        countriesOptions={makeCountryOptions(
                                            data,
                                        )}
                                        requestState={state}
                                        submit={submit}
                                        initialValues={vehicle}
                                        deleteVehicle={handleVehicleDelete}
                                        vehicleDeleteState={vehicleDeleteState}
                                        loading={refetchLoading}
                                        hasOpenEntryWithoutExit={
                                            vehicle.hasOpenEntryWithoutExit
                                        }
                                    />
                                </>
                            );
                        }}
                    />
                </ParkingaboLayoutWithHeader>
            )}
        />
    );
}
